import {
  ArrowRight,
  Bot,
  Building2,
  Check,
  CheckCircle2,
  CircleDot,
  Clock3,
  FileText,
  LayoutDashboard,
  Lock,
  Menu,
  MessageSquare,
  PackageCheck,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Send,
  Sparkles,
  Users,
  Waves,
  Workflow,
  X,
} from "lucide-react";
import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import type { DashboardData, Opportunity, PortalOverview, Project, RealtimeEvent, Task } from "../shared/types";
import { Badge } from "./components/ui/badge";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./components/ui/drawer";
import { Input } from "./components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Textarea } from "./components/ui/textarea";
import { cn } from "./lib/utils";
import "./styles.css";

const staffHeaders = {
  "content-type": "application/json",
  "x-mizusys-staff-email": "davi.borges@mizusys.com",
};

const stages = ["conversation", "qualified", "proposal", "won"] as const;
const industryOptions = [
  "Construction",
  "Home Improvements",
  "Architecture & Design",
  "Real Estate",
  "Manufacturing",
  "Professional Services",
  "Technology",
  "Healthcare",
  "Hospitality",
  "Retail",
  "Education",
  "Finance",
  "Logistics",
  "Nonprofit",
  "Government",
];
const homeImprovementServices = [
  "Kitchen remodeling",
  "Bathroom remodeling",
  "Roofing",
  "Siding",
  "Windows & doors",
  "Flooring",
  "Interior painting",
  "Exterior painting",
  "Drywall",
  "Plumbing",
  "Electrical",
  "HVAC",
  "Decks & patios",
  "Basement finishing",
  "Landscaping",
  "Handyman services",
];

function App() {
  const isPortal = window.location.pathname.startsWith("/portal");
  return isPortal ? <PortalApp /> : <StaffApp />;
}

type RealtimeConnectionStatus = "idle" | "connecting" | "connected" | "offline";

function useRealtime(path: string | null, onEvent: (event: RealtimeEvent) => void, enabled = true): RealtimeConnectionStatus {
  const [status, setStatus] = useState<RealtimeConnectionStatus>(enabled && path ? "connecting" : "idle");
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled || !path) {
      setStatus("idle");
      return;
    }

    const realtimePath = path;
    let closed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let heartbeatTimer: number | undefined;

    function connect() {
      const url = new URL(realtimePath, window.location.origin);
      url.protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      setStatus("connecting");
      socket = new WebSocket(url);

      socket.addEventListener("open", () => {
        setStatus("connected");
        heartbeatTimer = window.setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: "ping" }));
        }, 25_000);
      });

      socket.addEventListener("message", (message) => {
        if (typeof message.data !== "string") return;
        try {
          const event = JSON.parse(message.data) as RealtimeEvent;
          if (event.type === "realtime.connected" || event.type === "pong") return;
          onEventRef.current(event);
        } catch {
          // Ignore non-JSON frames such as low-level pong replies.
        }
      });

      socket.addEventListener("close", () => {
        if (heartbeatTimer) window.clearInterval(heartbeatTimer);
        if (closed) return;
        setStatus("offline");
        reconnectTimer = window.setTimeout(connect, 1500);
      });

      socket.addEventListener("error", () => {
        setStatus("offline");
      });
    }

    connect();

    return () => {
      closed = true;
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [enabled, path]);

  return status;
}

function staffRealtimePath(): string {
  const localHost = ["127.0.0.1", "localhost"].includes(window.location.hostname);
  return localHost ? "/api/staff/realtime?staffEmail=davi.borges@mizusys.com" : "/api/staff/realtime";
}

function realtimeLabel(status: RealtimeConnectionStatus): string {
  if (status === "connected") return "Live";
  if (status === "connecting") return "Connecting";
  if (status === "offline") return "Reconnecting";
  return "Live idle";
}

function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-md border border-white/30 bg-white shadow-sm",
        compact ? "size-9" : "size-11",
      )}
      aria-hidden="true"
    >
      <img src="/mizusys-mark.png" alt="" className="size-full object-cover" />
    </span>
  );
}

function StaffApp() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [status, setStatus] = useState("Loading Mizusys Flow...");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/staff/dashboard", { headers: staffHeaders });
      if (!response.ok) throw new Error(await readError(response));
      const next = (await response.json()) as DashboardData;
      setData(next);
      setActiveProjectId((current) => current || next.projects[0]?.id || null);
      setStatus("Workspace synced");
      setError(null);
    } catch (err) {
      setError(String(err));
      setStatus("API unavailable");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const realtimeStatus = useRealtime(staffRealtimePath(), () => void refresh(), Boolean(data));

  const selectedProject = data?.projects.find((project) => project.id === activeProjectId) || data?.projects[0] || null;
  const companyById = useMemo(
    () => new Map((data?.companies || []).map((company) => [company.id, company])),
    [data?.companies],
  );

  if (!data) {
    return (
      <main className="water-page grid min-h-screen place-items-center px-6 text-center">
        <div>
          <div className="mx-auto mb-5 grid size-16 place-items-center rounded-md bg-[#082432] text-cyan-200 shadow-xl">
            <Sparkles className="size-7" />
          </div>
          <p className="font-semibold">{status}</p>
          {error && <p className="mt-4 text-sm font-semibold text-destructive">{error}</p>}
        </div>
      </main>
    );
  }

  const openPipeline = data.opportunities.filter((opportunity) => opportunity.stage !== "lost").length;
  const activeProjects = data.projects.filter((project) => project.status === "active").length;

  return (
    <main
      className={cn(
        "water-page min-h-screen lg:grid",
        navCollapsed ? "lg:grid-cols-[4.75rem_minmax(0,1fr)]" : "lg:grid-cols-[17rem_minmax(0,1fr)]",
      )}
    >
      <aside
        className={cn(
          "deep-current hidden flex-col gap-7 text-cyan-50 transition-[padding] lg:sticky lg:top-0 lg:flex lg:h-screen",
          navCollapsed ? "items-center p-3" : "p-5",
        )}
      >
        <div className={cn("flex w-full items-center gap-3", navCollapsed ? "flex-col" : "justify-between")}>
          <StaffBrand collapsed={navCollapsed} />
          <Button
            variant="outline"
            size="icon"
            className="size-9 shrink-0 border-cyan-200/30 bg-white/10 text-cyan-50 hover:bg-white/15"
            onClick={() => setNavCollapsed((current) => !current)}
          >
            {navCollapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
            <span className="sr-only">{navCollapsed ? "Expand menu" : "Collapse menu"}</span>
          </Button>
        </div>
        <StaffNav collapsed={navCollapsed} />
        <StaffStatus status={status} realtimeStatus={realtimeStatus} collapsed={navCollapsed} />
      </aside>

      <StaffMobileBar status={status} realtimeStatus={realtimeStatus} />

      <section className={cn("grid min-w-0 gap-6 p-4 transition-[padding] sm:p-6 xl:p-8", agentOpen && "lg:pr-[25rem]")}>
        <header className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <Badge variant="outline" className="mb-4 border-cyan-300/70 bg-white/70 text-[#08677e]">
              Mizusys CRM
            </Badge>
            <h1 className="max-w-[20rem] break-words text-3xl font-black leading-tight tracking-normal text-[#07161d] sm:max-w-4xl sm:text-6xl xl:text-7xl">
              Be like water. Shape the system.
            </h1>
            <p className="mt-5 max-w-[21rem] text-base leading-7 text-muted-foreground sm:max-w-2xl">
              A calm operating surface for customer relationships, project delivery, client decisions, and durable
              handoffs.
            </p>
          </div>
        </header>

        <section id="dashboard" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Metric icon={<Building2 />} label="Companies" value={data.companies.length} note="Relationship map" />
          <Metric icon={<Users />} label="Contacts" value={data.contacts.length} note="Decision network" />
          <Metric icon={<Workflow />} label="Open pipeline" value={openPipeline} note="Active opportunities" />
          <Metric icon={<PackageCheck />} label="Active projects" value={activeProjects} note="Delivery flow" />
        </section>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-white p-3 text-sm font-semibold text-destructive">
            {error}
          </div>
        )}

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1.75fr)_minmax(20rem,0.75fr)]">
          <div id="pipeline" className="water-surface rounded-lg p-4">
            <SectionTitle icon={<Workflow />} title="Pipeline" action="Light proposals" />
            <div className="grid auto-cols-[minmax(12rem,1fr)] grid-flow-col gap-3 overflow-x-auto pb-1 xl:grid-flow-row xl:grid-cols-4">
              {stages.map((stage) => (
                <PipelineLane
                  key={stage}
                  stage={stage}
                  opportunities={data.opportunities.filter((opportunity) => opportunity.stage === stage)}
                  companyById={companyById}
                  onAdvance={(opportunity) => void advanceOpportunity(opportunity, refresh)}
                />
              ))}
            </div>
          </div>

          <QuickCreate onCreated={() => void refresh()} />
        </section>

        <section id="projects" className="grid gap-5 xl:grid-cols-[minmax(20rem,0.8fr)_minmax(0,1.4fr)]">
          <div className="water-surface rounded-lg p-4">
            <SectionTitle icon={<PackageCheck />} title="Delivery Projects" action={`${data.projects.length} total`} />
            <div className="grid gap-2">
              {data.projects.map((project) => (
                <button
                  className={cn(
                    "flex min-h-16 items-center justify-between rounded-md border bg-white px-3 text-left transition-colors hover:bg-accent",
                    selectedProject?.id === project.id && "border-[#08677e] bg-cyan-50",
                  )}
                  key={project.id}
                  onClick={() => setActiveProjectId(project.id)}
                >
                  <span className="grid gap-1">
                    <strong className="text-sm">{project.name}</strong>
                    <small className="text-xs text-muted-foreground">{companyById.get(project.company_id)?.name}</small>
                  </span>
                  <StatusBadge value={project.status} />
                </button>
              ))}
            </div>
          </div>

          <ProjectDetail data={data} project={selectedProject} onUpdated={() => void refresh()} />
        </section>

        <section id="messages" className="water-surface rounded-lg p-4">
          <SectionTitle icon={<MessageSquare />} title="Client-visible Timeline" action="Latest activity" />
          <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
            {data.messages.slice(0, 8).map((message) => (
              <article key={message.id} className="min-h-32 rounded-md border bg-white p-3">
                <span className="text-xs font-semibold text-muted-foreground">
                  {new Date(message.created_at).toLocaleDateString()}
                </span>
                <strong className="mt-2 block text-sm">{message.author_name}</strong>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{message.body}</p>
              </article>
            ))}
          </div>
        </section>
      </section>
      <AiAgentDrawer open={agentOpen} onOpenChange={setAgentOpen} />
    </main>
  );
}

function StaffBrand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className={cn("flex min-w-0 items-center gap-3", collapsed && "justify-center")}>
      <BrandMark />
      <div className={cn(collapsed && "sr-only")}>
        <strong className="block text-sm">Mizusys Flow</strong>
        <span className="block max-w-40 text-xs text-cyan-100/75">Relationship to delivery</span>
      </div>
    </div>
  );
}

function StaffNav({ collapsed = false, inDrawer = false }: { collapsed?: boolean; inDrawer?: boolean }) {
  const items = [
    { icon: <LayoutDashboard />, label: "Dashboard", href: "#dashboard" },
    { icon: <Workflow />, label: "Pipeline", href: "#pipeline" },
    { icon: <PackageCheck />, label: "Projects", href: "#projects" },
    { icon: <MessageSquare />, label: "Messages", href: "#messages" },
  ];

  return (
    <nav className="grid gap-1 text-sm">
      {items.map((item) =>
        inDrawer ? (
          <DrawerClose key={item.href} asChild>
            <NavItem {...item} />
          </DrawerClose>
        ) : (
          <NavItem key={item.href} {...item} collapsed={collapsed} />
        ),
      )}
    </nav>
  );
}

function StaffStatus({
  collapsed = false,
  status,
  realtimeStatus,
}: {
  collapsed?: boolean;
  status: string;
  realtimeStatus: RealtimeConnectionStatus;
}) {
  if (collapsed) {
    return (
      <div
        className="mt-auto grid size-10 place-items-center rounded-md border border-cyan-200/20 bg-white/5 text-cyan-100/85"
        title={`${status} · ${realtimeLabel(realtimeStatus)}`}
      >
        <CircleDot className="size-4 text-cyan-200" />
      </div>
    );
  }

  return (
    <div className="mt-auto rounded-md border border-cyan-200/20 bg-white/5 p-3 text-sm text-cyan-100/85">
      <div className="flex items-center gap-2">
        <CircleDot className="size-4 text-cyan-200" />
        <span>{status} · {realtimeLabel(realtimeStatus)}</span>
      </div>
    </div>
  );
}

function StaffMobileBar({
  status,
  realtimeStatus,
}: {
  status: string;
  realtimeStatus: RealtimeConnectionStatus;
}) {
  return (
    <div className="deep-current sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-cyan-200/15 p-3 text-cyan-50 lg:hidden">
      <SideMenuDrawer status={status} realtimeStatus={realtimeStatus} />
      <StaffBrand />
      <span className="size-10" aria-hidden="true" />
    </div>
  );
}

function SideMenuDrawer({
  status,
  realtimeStatus,
}: {
  status: string;
  realtimeStatus: RealtimeConnectionStatus;
}) {
  return (
    <Drawer direction="left">
      <DrawerTrigger asChild>
        <Button variant="outline" size="icon" className="border-cyan-200/30 bg-white/10 text-cyan-50 hover:bg-white/15">
          <Menu className="size-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent
        showHandle={false}
        className="deep-current inset-y-0 left-0 right-auto top-0 mt-0 h-full w-[min(18rem,calc(100vw-2rem))] rounded-l-none rounded-r-lg border-y-0 border-l-0 border-r border-cyan-200/20 px-4 pb-4 text-cyan-50"
      >
        <DrawerHeader className="px-0">
          <DrawerTitle className="text-cyan-50">Mizusys Flow</DrawerTitle>
          <DrawerDescription className="text-cyan-100/75">Relationship to delivery</DrawerDescription>
        </DrawerHeader>
        <StaffNav inDrawer />
        <StaffStatus status={status} realtimeStatus={realtimeStatus} />
      </DrawerContent>
    </Drawer>
  );
}

function AiAgentDrawer({ onOpenChange, open }: { onOpenChange: (open: boolean) => void; open: boolean }) {
  return (
    <Drawer direction="right" modal={false} open={open} onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button
          className="fixed bottom-5 right-5 z-40 size-12 rounded-md bg-[#08677e] shadow-xl hover:bg-[#07586d]"
          size="icon"
        >
          <Bot className="size-5" />
          <span className="sr-only">Open AI agent</span>
        </Button>
      </DrawerTrigger>
      <DrawerContent
        showHandle={false}
        showOverlay={false}
        className="inset-y-0 left-auto right-0 top-0 mt-0 flex h-full w-[min(24rem,calc(100vw-1rem))] flex-col rounded-l-lg rounded-r-none border-y-0 border-l border-r-0"
      >
        <DrawerHeader>
          <div className="flex items-start gap-3">
            <div className="grid size-10 place-items-center rounded-md bg-[#082432] text-cyan-200">
              <Bot className="size-5" />
            </div>
            <div>
              <DrawerTitle>Mizusys AI Agent</DrawerTitle>
              <DrawerDescription>Agent workspace</DrawerDescription>
            </div>
          </div>
        </DrawerHeader>
        <div className="grid flex-1 content-start gap-3 overflow-y-auto px-4 pb-2">
          <div className="rounded-md border bg-white p-3 text-sm leading-6 shadow-sm">
            Ready beside the CRM canvas.
          </div>
          <div className="rounded-md border bg-muted p-3 text-sm text-muted-foreground">
            Tools are offline.
          </div>
        </div>
        <DrawerFooter>
          <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <Input disabled placeholder="Ask the Mizusys agent" />
            <Button disabled>
              <Send /> Send
            </Button>
          </div>
          <DrawerClose asChild>
            <Button variant="outline">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

function NavItem({
  collapsed = false,
  icon,
  label,
  href,
}: {
  collapsed?: boolean;
  icon: ReactNode;
  label: string;
  href: string;
}) {
  return (
    <a
      href={href}
      title={collapsed ? label : undefined}
      className={cn(
        "flex items-center justify-center gap-3 rounded-md px-3 py-2 text-cyan-50/90 hover:bg-white/10 lg:justify-start",
        collapsed && "size-10 px-0 lg:justify-center",
      )}
    >
      <span className="[&_svg]:size-4">{icon}</span>
      <span className={cn(collapsed && "sr-only")}>{label}</span>
    </a>
  );
}

function Metric({ icon, label, value, note }: { icon: ReactNode; label: string; value: number; note: string }) {
  return (
    <Card className="current-line overflow-hidden rounded-lg border-cyan-100/90 bg-white/90">
      <CardHeader className="space-y-3 p-4">
        <div className="flex items-start justify-between">
          <div className="rounded-md border bg-white p-2 text-[#08677e] shadow-sm [&_svg]:size-5">{icon}</div>
          <Waves className="size-5 text-cyan-500/70" />
        </div>
        <div>
          <CardDescription>{label}</CardDescription>
          <CardTitle className="mt-2 text-3xl font-black">{value}</CardTitle>
          <p className="mt-2 text-xs font-medium text-muted-foreground">{note}</p>
        </div>
      </CardHeader>
    </Card>
  );
}

function PipelineLane({
  stage,
  opportunities,
  companyById,
  onAdvance,
}: {
  stage: Opportunity["stage"];
  opportunities: Opportunity[];
  companyById: Map<string, { name: string }>;
  onAdvance: (opportunity: Opportunity) => void;
}) {
  const value = opportunities.reduce((sum, opportunity) => sum + opportunity.value_cents, 0);
  return (
    <div className="min-w-48 rounded-md border bg-muted/70 p-3 xl:min-w-0">
      <div className="mb-3 flex items-center justify-between gap-2 text-xs font-bold capitalize text-muted-foreground">
        <span>{stage}</span>
        <span>{money(value)}</span>
      </div>
      <div className="grid gap-2">
        {opportunities.map((opportunity) => (
          <article key={opportunity.id} className="rounded-md border bg-white p-3 shadow-sm">
            <strong className="block text-sm leading-5">{opportunity.name}</strong>
            <span className="mt-1 block text-xs text-muted-foreground">
              {companyById.get(opportunity.company_id)?.name || "Unknown"}
            </span>
            <div className="mt-4 flex items-center justify-between">
              <b className="text-sm">{money(opportunity.value_cents)}</b>
              <Button size="icon" variant="outline" className="size-8" onClick={() => onAdvance(opportunity)}>
                <ArrowRight className="size-4" />
                <span className="sr-only">Advance {opportunity.name}</span>
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function QuickCreate({ onCreated }: { onCreated: () => void }) {
  const [mode, setMode] = useState<"company" | "intake">("company");
  const [busy, setBusy] = useState(false);
  const [industry, setIndustry] = useState("");
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const showHomeServices = normalizeIndustry(industry) === "home improvements";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const form = new FormData(event.currentTarget);
    const payload: Record<string, unknown> = Object.fromEntries(form.entries());
    if (mode === "company") {
      payload.industry = industry;
      payload.homeImprovementServices = selectedServices;
    }
    const endpoint = mode === "company" ? "/api/staff/companies" : "/api/intake";
    const response = await fetch(endpoint, {
      method: "POST",
      headers: mode === "company" ? staffHeaders : { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (!response.ok) {
      alert(await readError(response));
      return;
    }
    event.currentTarget.reset();
    setIndustry("");
    setSelectedServices([]);
    onCreated();
  }

  function toggleService(service: string) {
    setSelectedServices((current) =>
      current.includes(service) ? current.filter((item) => item !== service) : [...current, service],
    );
  }

  return (
    <form className="water-surface grid content-start gap-3 rounded-lg p-4" onSubmit={(event) => void submit(event)}>
      <SectionTitle icon={<Plus />} title="Create" action={mode === "company" ? "Staff" : "Public intake"} />
      <Tabs value={mode} onValueChange={(value) => setMode(value as "company" | "intake")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="company">Company</TabsTrigger>
          <TabsTrigger value="intake">Intake</TabsTrigger>
        </TabsList>
      </Tabs>
      {mode === "company" ? (
        <>
          <Input name="name" placeholder="Company name" required />
          <Input name="website" placeholder="Website" />
          <IndustryInput value={industry} onChange={setIndustry} />
          {showHomeServices && (
            <HomeImprovementServices selected={selectedServices} onToggle={toggleService} />
          )}
          <Textarea name="notes" placeholder="Relationship notes" />
        </>
      ) : (
        <>
          <Input name="name" placeholder="Contact name" required />
          <Input name="email" placeholder="Email" type="email" required />
          <Input name="company" placeholder="Company" required />
          <Textarea name="message" placeholder="What system should Mizusys help shape?" required />
        </>
      )}
      <Button disabled={busy} className="bg-cyan-300 text-[#06151c] hover:bg-cyan-200">
        <Plus /> {busy ? "Creating..." : "Create record"}
      </Button>
    </form>
  );
}

function IndustryInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="grid gap-2">
      <Input
        name="industry"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Industry"
        list="mizusys-industry-options"
        autoComplete="off"
      />
      <datalist id="mizusys-industry-options">
        {industryOptions.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
      <div className="flex flex-wrap gap-1.5" aria-label="Common industries">
        {industryOptions.slice(0, 7).map((option) => (
          <Button
            key={option}
            type="button"
            variant={normalizeIndustry(value) === normalizeIndustry(option) ? "default" : "outline"}
            size="sm"
            className={cn(
              "h-7 px-2 text-xs",
              normalizeIndustry(value) === normalizeIndustry(option) && "bg-[#08677e] hover:bg-[#07586d]",
            )}
            onClick={() => onChange(option)}
          >
            {option}
          </Button>
        ))}
      </div>
    </div>
  );
}

function HomeImprovementServices({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (service: string) => void;
}) {
  return (
    <div className="rounded-md border bg-white/70 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <strong className="text-sm">Home improvement services</strong>
          <p className="text-xs text-muted-foreground">Select all that apply.</p>
        </div>
        {selected.length > 0 && (
          <Badge variant="secondary" className="shrink-0">
            {selected.length} selected
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {homeImprovementServices.map((service) => {
          const active = selected.includes(service);
          return (
            <Button
              key={service}
              type="button"
              variant={active ? "default" : "outline"}
              size="sm"
              className={cn(
                "h-8 gap-1.5 px-2 text-xs",
                active && "bg-cyan-300 text-[#06151c] hover:bg-cyan-200",
              )}
              onClick={() => onToggle(service)}
            >
              {active ? <Check className="size-3.5" /> : <Plus className="size-3.5" />}
              {service}
            </Button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {selected.map((service) => (
            <Badge key={service} variant="outline" className="gap-1 border-cyan-300 bg-cyan-50 text-[#07586d]">
              {service}
              <button type="button" aria-label={`Remove ${service}`} onClick={() => onToggle(service)}>
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function normalizeIndustry(value: string): string {
  return value.trim().toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ");
}

function ProjectDetail({ data, project, onUpdated }: { data: DashboardData; project: Project | null; onUpdated: () => void }) {
  const [message, setMessage] = useState("");
  if (!project) {
    return <div className="water-surface rounded-lg p-4 text-sm text-muted-foreground">No project selected.</div>;
  }
  const milestones = data.milestones.filter((milestone) => milestone.project_id === project.id);
  const tasks = data.tasks.filter((task) => task.project_id === project.id);
  const deliverables = data.deliverables.filter((deliverable) => deliverable.project_id === project.id);

  async function completeTask(task: Task) {
    await fetch(`/api/staff/tasks/${task.id}`, {
      method: "PATCH",
      headers: staffHeaders,
      body: JSON.stringify({ status: task.status === "done" ? "in_progress" : "done" }),
    });
    onUpdated();
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!message.trim()) return;
    await fetch("/api/staff/messages", {
      method: "POST",
      headers: staffHeaders,
      body: JSON.stringify({ projectId: project?.id, companyId: project?.company_id, audience: "client", message }),
    });
    setMessage("");
    onUpdated();
  }

  return (
    <div className="water-surface rounded-lg p-4">
      <SectionTitle icon={<FileText />} title={project.name} action={project.phase} />
      <p className="max-w-3xl text-sm leading-6 text-muted-foreground">{project.summary || "No summary yet."}</p>
      <div className="my-5 grid gap-3 md:grid-cols-3">
        {milestones.map((milestone) => (
          <div key={milestone.id} className="rounded-md border bg-white p-3">
            <CheckCircle2 className="mb-3 size-4 text-[#08677e]" />
            <strong className="block text-sm">{milestone.name}</strong>
            <span className="mt-1 block text-xs text-muted-foreground">{milestone.due_date || "No date"}</span>
          </div>
        ))}
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <h3 className="mb-3 text-sm font-bold">Tasks</h3>
          <div className="grid gap-2">
            {tasks.map((task) => (
              <button
                className="flex items-center justify-between gap-3 rounded-md border bg-white p-3 text-left hover:bg-accent"
                key={task.id}
                onClick={() => void completeTask(task)}
              >
                <span className="grid gap-1">
                  <strong className="text-sm">{task.title}</strong>
                  <small className="text-xs text-muted-foreground">
                    {task.client_action_required ? "Client action" : task.priority}
                  </small>
                </span>
                <StatusBadge value={task.status} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <h3 className="mb-3 text-sm font-bold">Deliverables</h3>
          <div className="grid gap-2">
            {deliverables.map((deliverable) => (
              <div className="flex items-center gap-3 rounded-md bg-muted p-3" key={deliverable.id}>
                <FileText className="size-4 text-[#08677e]" />
                <span className="grid gap-1">
                  <strong className="text-sm">{deliverable.title}</strong>
                  <small className="text-xs text-muted-foreground">{deliverable.status}</small>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <form className="mt-5 grid gap-2 sm:grid-cols-[1fr_auto]" onSubmit={(event) => void sendMessage(event)}>
        <Input
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Send a client-visible update"
        />
        <Button size="icon" variant="outline" aria-label="Send message">
          <Send className="size-4" />
        </Button>
      </form>
    </div>
  );
}

function PortalApp() {
  const [token, setToken] = useState(localStorage.getItem("mizusys_portal_token") || "");
  const [email, setEmail] = useState("");
  const [overview, setOverview] = useState<PortalOverview | null>(null);
  const [status, setStatus] = useState("Welcome to your Mizusys portal.");

  useEffect(() => {
    const inviteToken = new URLSearchParams(window.location.search).get("token");
    if (!inviteToken) return;
    void fetch("/api/portal/session", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token: inviteToken }),
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await readError(response));
        return response.json() as Promise<{ token: string }>;
      })
      .then((session) => {
        localStorage.setItem("mizusys_portal_token", session.token);
        setToken(session.token);
        window.history.replaceState({}, "", "/portal");
      })
      .catch((error) => setStatus(String(error)));
  }, []);

  const refreshOverview = useCallback(async () => {
    if (!token) return;
    await fetch("/api/portal/overview", { headers: { authorization: `Bearer ${token}` } })
      .then(async (response) => {
        if (!response.ok) throw new Error(await readError(response));
        return response.json() as Promise<PortalOverview>;
      })
      .then(setOverview)
      .catch((error) => setStatus(String(error)));
  }, [token]);

  useEffect(() => {
    void refreshOverview();
  }, [refreshOverview]);

  const portalRealtimePath = token ? `/api/portal/realtime?token=${encodeURIComponent(token)}` : null;
  useRealtime(portalRealtimePath, () => void refreshOverview(), Boolean(token && overview));

  async function requestLink(event: FormEvent) {
    event.preventDefault();
    await fetch("/api/portal/login-request", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
    setStatus("If portal access exists for that email, a sign-in link is on its way.");
  }

  if (!overview) {
    return (
      <main className="portal-scene grid min-h-screen place-items-center p-5 text-cyan-50">
        <section className="w-full max-w-[21rem] sm:max-w-3xl">
          <div className="mb-9 flex items-center gap-3">
            <BrandMark />
            <div>
              <strong className="block">Mizusys Portal</strong>
              <span className="text-sm text-cyan-100/80">{status}</span>
            </div>
          </div>
          <h1 className="max-w-[20rem] break-words text-3xl font-black leading-tight tracking-normal text-white sm:max-w-3xl sm:text-6xl">
            Move clearly. Adapt calmly. Deliver with flow.
          </h1>
          <form className="mt-9 grid gap-3 rounded-lg border border-white/20 bg-[#06151c]/55 p-3 backdrop-blur md:grid-cols-[auto_1fr_auto]" onSubmit={(event) => void requestLink(event)}>
            <Lock className="hidden self-center text-cyan-200 md:block" />
            <Input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              placeholder="you@company.com"
              required
              className="border-white/15 bg-white text-foreground"
            />
            <Button className="bg-cyan-300 text-[#06151c] hover:bg-cyan-200">Send link</Button>
          </form>
        </section>
      </main>
    );
  }

  const project = overview.projects[0];
  return (
    <main className="water-page min-h-screen p-4 sm:p-6 xl:p-8">
      <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div>
            <strong className="block">{overview.companies[0]?.name || "Client portal"}</strong>
            <span className="text-sm text-muted-foreground">Shared with {overview.contact.name}</span>
          </div>
        </div>
        <StatusBadge value={project?.status || "ready"} />
      </header>
      <section className="deep-current mb-5 rounded-lg p-6 text-cyan-50 sm:p-8">
        <Badge variant="outline" className="mb-4 border-cyan-200/30 bg-white/10 text-cyan-50">
          Current project
        </Badge>
        <h1 className="text-4xl font-black leading-tight tracking-normal text-white sm:text-5xl">
          {project?.name || "No active project yet"}
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-cyan-100/80">
          {project?.summary || "Mizusys will share project progress here once delivery begins."}
        </p>
      </section>
      <section className="grid gap-5 lg:grid-cols-2">
        <PortalPanel icon={<CheckCircle2 />} title="Milestones" action={`${overview.milestones.length} shared`}>
          {overview.milestones.map((milestone) => (
            <PortalRow key={milestone.id} title={milestone.name} aside={<StatusBadge value={milestone.status} />} />
          ))}
        </PortalPanel>
        <PortalPanel icon={<Clock3 />} title="Needs Your Input" action={`${overview.tasks.length} tasks`}>
          {overview.tasks.map((task) => (
            <PortalRow key={task.id} title={task.title} aside={task.due_date || "No due date"} />
          ))}
        </PortalPanel>
        <PortalPanel icon={<FileText />} title="Deliverables" action={`${overview.deliverables.length} files`}>
          {overview.deliverables.map((deliverable) => (
            <PortalRow key={deliverable.id} title={deliverable.title} aside={<StatusBadge value={deliverable.status} />} />
          ))}
        </PortalPanel>
        <PortalMessage token={token} projectId={project?.id} />
      </section>
    </main>
  );
}

function PortalPanel({ icon, title, action, children }: { icon: ReactNode; title: string; action: string; children: ReactNode }) {
  return (
    <div className="water-surface rounded-lg p-4">
      <SectionTitle icon={icon} title={title} action={action} />
      <div className="grid gap-2">{children}</div>
    </div>
  );
}

function PortalRow({ title, aside }: { title: string; aside: ReactNode }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-3 rounded-md border bg-white px-3">
      <strong className="text-sm">{title}</strong>
      <span className="text-xs text-muted-foreground">{aside}</span>
    </div>
  );
}

function PortalMessage({ token, projectId }: { token: string; projectId?: string }) {
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!projectId || !message.trim()) return;
    await fetch("/api/portal/messages", {
      method: "POST",
      headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
      body: JSON.stringify({ projectId, message }),
    });
    setMessage("");
  }
  return (
    <form className="water-surface grid gap-3 rounded-lg p-4" onSubmit={(event) => void submit(event)}>
      <SectionTitle icon={<MessageSquare />} title="Message Mizusys" action="Client portal" />
      <Textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        placeholder="Share an update, decision, or question"
      />
      <Button className="bg-cyan-300 text-[#06151c] hover:bg-cyan-200">
        <Send /> Send message
      </Button>
    </form>
  );
}

function SectionTitle({ icon, title, action }: { icon: ReactNode; title: string; action: string }) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2 text-[#08677e]">
        <span className="[&_svg]:size-4">{icon}</span>
        <h2 className="truncate text-sm font-black text-foreground">{title}</h2>
      </div>
      <span className="shrink-0 text-xs font-semibold text-muted-foreground">{action}</span>
    </div>
  );
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.replaceAll("_", " ");
  return (
    <Badge
      variant="secondary"
      className={cn(
        "capitalize",
        ["blocked", "lost"].includes(value) && "bg-rose-100 text-rose-800",
        ["done", "complete", "won"].includes(value) && "bg-cyan-100 text-cyan-900",
        ["proposal", "review"].includes(value) && "bg-amber-100 text-amber-800",
      )}
    >
      {normalized}
    </Badge>
  );
}

async function advanceOpportunity(opportunity: Opportunity, refresh: () => Promise<void>) {
  const currentIndex = stages.findIndex((stage) => stage === opportunity.stage);
  const nextStage = stages[Math.min(Math.max(currentIndex, 0) + 1, stages.length - 1)];
  await fetch(`/api/staff/opportunities/${opportunity.id}/stage`, {
    method: "PATCH",
    headers: staffHeaders,
    body: JSON.stringify({ stage: nextStage }),
  });
  await refresh();
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

async function readError(response: Response) {
  try {
    const data = (await response.json()) as { error?: string };
    return data.error || response.statusText;
  } catch {
    return response.statusText;
  }
}

createRoot(document.getElementById("root")!).render(<App />);
